// CustomerManagement.tsx
// Admin Customer Management Component with full functionality

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowPathIcon,
  FunnelIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { adminService, Customer as APICustomer } from '../../services/adminService';
import './CustomerManagement.css';

interface Address {
  id: string;
  type: 'private' | 'business' | 'billing' | 'shipping';
  street: string;
  city: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
}

interface Customer {
  id: string;
  customerNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  customerType: 'private' | 'business';
  status: 'active' | 'inactive' | 'blocked';
  registeredAt: string;
  lastActivity: string;
  totalBookings: number;
  totalRevenue: number;
  addresses: Address[];
  notes: string[];
  tags: string[];
  newsletter: boolean;
  verified: boolean;
  vip: boolean;
}

const CustomerManagement: React.FC = () => {
  const location = useLocation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    business: 0,
    vip: 0,
    totalRevenue: 0,
    averageRevenue: 0
  });

  // Form states for new/edit customer
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    customerType: 'private' as 'private' | 'business',
    newsletter: false
  });

  // Separate state for managing addresses
  const [customerAddresses, setCustomerAddresses] = useState<Address[]>([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);

  // Helper functions for address management
  const addNewAddress = () => {
    const newAddress: Address = {
      id: `new-${Date.now()}`,
      type: 'private',
      street: '',
      city: '',
      postalCode: '',
      country: 'Deutschland',
      isDefault: customerAddresses.length === 0
    };
    setEditingAddress(newAddress);
    setShowAddressForm(true);
  };

  const saveAddress = () => {
    if (!editingAddress) return;

    const index = customerAddresses.findIndex(a => a.id === editingAddress.id);
    if (index >= 0) {
      // Update existing address
      const updatedAddresses = [...customerAddresses];
      updatedAddresses[index] = editingAddress;
      setCustomerAddresses(updatedAddresses);
    } else {
      // Add new address
      setCustomerAddresses([...customerAddresses, editingAddress]);
    }

    setEditingAddress(null);
    setShowAddressForm(false);
  };

  const deleteAddress = (id: string) => {
    setCustomerAddresses(customerAddresses.filter(a => a.id !== id));
  };

  const setAsDefault = (id: string) => {
    setCustomerAddresses(customerAddresses.map(a => ({
      ...a,
      isDefault: a.id === id
    })));
  };

  // Load customers on mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Check if we should open "New Customer" dialog from navigation state
  useEffect(() => {
    if (location.state?.openDialog) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        customerType: 'private',
        newsletter: false
      });
      setCustomerAddresses([]);
      setShowNewCustomerModal(true);
      // Reset state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Real API call
      const params = {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        vip: undefined,
        page: 1,
        pageSize: 100
      };
      
      const response = await adminService.getCustomers(params);

      // Transform API data to component format
      // Backend gibt PascalCase zurück, Frontend erwartet camelCase
      const responseData: any = response;
      const customersData = responseData.data?.Customers || responseData.data?.customers || response.customers || [];
      const transformedCustomers: Customer[] = customersData.map((c: any) => ({
        id: (c.Id || c.id)?.toString() || '',
        customerNumber: `K-${((c.Id || c.id) || 0).toString().padStart(6, '0')}`,
        firstName: c.FirstName || c.firstName || '',
        lastName: c.LastName || c.lastName || '',
        email: c.Email || c.email || '',
        phone: c.Phone || c.phone || '',
        company: c.CompanyName || c.companyName || c.Company || c.company || '',
        customerType: ((c.CustomerType || c.customerType || 'private').toLowerCase()) as 'private' | 'business',
        status: ((c.Status || c.status || 'active').toLowerCase()) as any,
        registeredAt: c.RegisteredAt || c.registeredAt || c.CreatedAt || c.createdAt || '',
        lastActivity: c.LastActivity || c.lastActivity || c.LastBookingDate || c.lastBookingDate || c.CreatedAt || c.createdAt || '',
        totalBookings: c.TotalBookings || c.totalBookings || c.BookingCount || c.bookingCount || 0,
        totalRevenue: c.TotalSpent || c.totalSpent || c.TotalRevenue || c.totalRevenue || 0,
        addresses: (c.Addresses || c.addresses || []).map((a: any) => ({
          id: (a.Id || a.id)?.toString() || '',
          type: (a.AddressType || a.addressType || 'billing') as any,
          street: a.Street || a.street || '',
          city: a.City || a.city || '',
          postalCode: a.PostalCode || a.postalCode || '',
          country: a.Country || a.country || 'Deutschland',
          isDefault: a.IsDefault || a.isDefault || false
        })),
        // T-084: API liefert notes als String (camelCase), nie als Array; ohne Array-Absicherung crasht das Details-Modal (notes.map)
        notes: Array.isArray(c.notes) ? c.notes : ((c.Notes || c.notes) ? [String(c.Notes || c.notes)] : []),
        tags: (c.IsVip || c.isVip) ? ['VIP'] : [],
        newsletter: c.Newsletter || c.newsletter || false,
        verified: c.Verified || c.verified || true,
        vip: c.IsVip || c.isVip || false
      }));
      
      setCustomers(transformedCustomers);
      
      // Calculate statistics
      const stats = {
        total: transformedCustomers.length,
        active: transformedCustomers.filter(c => c.status === 'active').length,
        business: transformedCustomers.filter(c => c.customerType === 'business').length,
        vip: transformedCustomers.filter(c => c.vip).length,
        totalRevenue: transformedCustomers.reduce((sum, c) => sum + c.totalRevenue, 0),
        averageRevenue: transformedCustomers.length > 0 
          ? transformedCustomers.reduce((sum, c) => sum + c.totalRevenue, 0) / transformedCustomers.length 
          : 0
      };
      setStats(stats);
      
    } catch (error: any) {
      console.error('❌ Error fetching customers:', error);
      setError('Fehler beim Laden der Kundendaten. Bitte Backend prüfen.');

      // ❌ KEIN FALLBACK ZU MOCK-DATEN!
      setCustomers([]);
      setStats({
        total: 0,
        active: 0,
        business: 0,
        vip: 0,
        totalRevenue: 0,
        averageRevenue: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (customerList: Customer[]) => {
    const stats = {
      total: customerList.length,
      active: customerList.filter(c => c.status === 'active').length,
      business: customerList.filter(c => c.customerType === 'business').length,
      vip: customerList.filter(c => c.vip).length,
      totalRevenue: customerList.reduce((sum, c) => sum + c.totalRevenue, 0),
      averageRevenue: customerList.length > 0 
        ? customerList.reduce((sum, c) => sum + c.totalRevenue, 0) / customerList.length 
        : 0
    };
    setStats(stats);
  };

  const handleSaveCustomer = async () => {
    try {
      console.log('Saving customer with addresses:', customerAddresses);

      const customerData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.company,
        customerType: formData.customerType === 'business' ? 'Business' : 'Private',
        isVip: false,
        // Send addresses array
        addresses: customerAddresses.map(addr => ({
          type: addr.type === 'private' ? 'Private' :
                addr.type === 'business' ? 'Business' :
                addr.type === 'billing' ? 'Billing' :
                addr.type === 'shipping' ? 'Shipping' : 'Private',
          street: addr.street,
          city: addr.city,
          postalCode: addr.postalCode,
          country: addr.country || 'Deutschland',
          isDefault: addr.isDefault
        }))
      };

      console.log('Customer data being sent:', customerData);

      // For backward compatibility, also send first address as flat fields if exists
      const defaultAddr = customerAddresses.find(a => a.isDefault) || customerAddresses[0];
      if (defaultAddr) {
        customerData.street = defaultAddr.street;
        customerData.city = defaultAddr.city;
        customerData.postalCode = defaultAddr.postalCode;
        customerData.country = defaultAddr.country || 'Deutschland';
      }
      
      if (selectedCustomer) {
        // Update existing customer
        await adminService.updateCustomer(parseInt(selectedCustomer.id), customerData);
        alert('Kunde erfolgreich aktualisiert');
      } else {
        // Create new customer  
        await adminService.createCustomer(customerData);
        alert('Neuer Kunde erfolgreich angelegt');
      }
      
      setShowEditModal(false);
      setShowNewCustomerModal(false);
      fetchCustomers();
    } catch (err) {
      console.error('Error saving customer:', err);
      alert('Fehler beim Speichern des Kunden');
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (window.confirm('Möchten Sie diesen Kunden wirklich löschen?')) {
      try {
        await adminService.deleteCustomer(parseInt(customerId));
        alert('Kunde erfolgreich gelöscht');
        fetchCustomers();
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert('Fehler beim Löschen des Kunden');
      }
    }
  };

  const handleSendEmail = (customer: Customer) => {
    window.location.href = `mailto:${customer.email}`;
  };

  // T-081 Altfall-Übernahme (v1.8.17): Zugangslink (Passwort festlegen, 7 Tage gültig) an den Kunden senden
  const [sendingAccessLinkId, setSendingAccessLinkId] = useState<string | null>(null);
  const handleSendAccessLink = async (customer: Customer) => {
    if (!customer.email) {
      alert('Dieser Kunde hat keine E-Mail-Adresse.');
      return;
    }
    const ok = window.confirm(
      `Zugangslink an ${customer.firstName} ${customer.lastName} senden?\n\n` +
      `Empfänger: ${customer.email}\n` +
      `Der Gast erhält eine E-Mail mit einem Link zum Festlegen seines Passworts (7 Tage gültig, einmal verwendbar). ` +
      `Danach kann er sich mit seiner E-Mail-Adresse anmelden und seine Buchungen einsehen.`
    );
    if (!ok) return;
    try {
      setSendingAccessLinkId(customer.id);
      const result = await adminService.sendCustomerAccessLink(parseInt(customer.id));
      alert(result?.message || `Zugangslink an ${customer.email} gesendet.`);
    } catch (err: any) {
      console.error('Error sending access link:', err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Unbekannter Fehler';
      alert(`Zugangslink konnte nicht gesendet werden: ${msg}`);
    } finally {
      setSendingAccessLinkId(null);
    }
  };

  const handleCall = (customer: Customer) => {
    window.location.href = `tel:${customer.phone}`;
  };

  // Filter customers
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = searchTerm === '' ||
      customer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customerNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === 'all' ||
                        customer.customerType === typeFilter ||
                        (typeFilter === 'vip' && customer.vip);
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  // Sort customers
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let comparison = 0;
    
    switch(sortBy) {
      case 'name':
        comparison = `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        break;
      case 'revenue':
        comparison = a.totalRevenue - b.totalRevenue;
        break;
      case 'bookings':
        comparison = a.totalBookings - b.totalBookings;
        break;
      case 'activity':
        comparison = new Date(a.lastActivity).getTime() - new Date(b.lastActivity).getTime();
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'active':
        return <span className="status-badge status-active">Aktiv</span>;
      case 'inactive':
        return <span className="status-badge status-inactive">Inaktiv</span>;
      case 'blocked':
        return <span className="status-badge status-blocked">Gesperrt</span>;
      default:
        return null;
    }
  };

  const getCustomerTypeBadge = (type: string) => {
    return type === 'business' 
      ? <span className="type-badge type-business">Geschäftskunde</span>
      : <span className="type-badge type-private">Privatkunde</span>;
  };

  if (loading) {
    return (
      <div className="customer-management loading">
        <div className="spinner">
          <ArrowPathIcon className="w-12 h-12 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-management error">
        <p className="error-message">{error}</p>
        <button onClick={fetchCustomers} className="btn-retry">
          <ArrowPathIcon className="w-5 h-5" />
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="customer-management">
      {/* Header with Statistics */}
      <div className="customer-header">
        <div className="header-top">
          <h2>Kundenverwaltung</h2>
          <button 
            onClick={() => {
              setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                company: '',
                customerType: 'private',
                newsletter: false
              });
              // Initialize empty addresses for new customer
              setCustomerAddresses([]);
              setShowNewCustomerModal(true);
            }}
            className="btn-new-customer"
          >
            <UserPlusIcon className="w-5 h-5" />
            Neuer Kunde
          </button>
        </div>
        
        <div className="stats-row">
          <div
            className={`stat-card ${typeFilter === 'all' && statusFilter === 'all' ? 'stat-card-active' : ''}`}
            onClick={() => {
              setTypeFilter('all');
              setStatusFilter('all');
            }}
            style={{ cursor: 'pointer' }}
          >
            <UserGroupIcon className="stat-icon" />
            <div className="stat-content">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Gesamt</span>
            </div>
          </div>
          <div
            className={`stat-card ${statusFilter === 'active' ? 'stat-card-active' : ''}`}
            onClick={() => {
              setStatusFilter('active');
              setTypeFilter('all');
            }}
            style={{ cursor: 'pointer' }}
          >
            <CheckBadgeIcon className="stat-icon text-green" />
            <div className="stat-content">
              <span className="stat-value">{stats.active}</span>
              <span className="stat-label">Aktiv</span>
            </div>
          </div>
          <div
            className={`stat-card ${typeFilter === 'business' ? 'stat-card-active' : ''}`}
            onClick={() => {
              setTypeFilter('business');
              setStatusFilter('all');
            }}
            style={{ cursor: 'pointer' }}
          >
            <BuildingOfficeIcon className="stat-icon text-blue" />
            <div className="stat-content">
              <span className="stat-value">{stats.business}</span>
              <span className="stat-label">Business</span>
            </div>
          </div>
          <div
            className={`stat-card ${typeFilter === 'vip' ? 'stat-card-active' : ''}`}
            onClick={() => {
              setTypeFilter('vip');
              setStatusFilter('all');
            }}
            style={{ cursor: 'pointer' }}
          >
            <span className="vip-icon">VIP</span>
            <div className="stat-content">
              <span className="stat-value">{stats.vip}</span>
              <span className="stat-label">VIP-Kunden</span>
            </div>
          </div>
          <div className="stat-card" style={{ cursor: 'default' }}>
            <ChartBarIcon className="stat-icon text-purple" />
            <div className="stat-content">
              <span className="stat-value">{stats.totalRevenue.toFixed(0)} €</span>
              <span className="stat-label">Gesamtumsatz</span>
            </div>
          </div>
          <div className="stat-card" style={{ cursor: 'default' }}>
            <CreditCardIcon className="stat-icon text-yellow" />
            <div className="stat-content">
              <span className="stat-value">{stats.averageRevenue.toFixed(0)} €</span>
              <span className="stat-label">Ø Umsatz</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="customer-filters">
        <div className="filter-row">
          <div className="search-box">
            <MagnifyingGlassIcon className="w-5 h-5" />
            <input
              type="text"
              placeholder="Suche nach Name, E-Mail, Firma, Kundennummer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Typen</option>
            <option value="private">Privatkunden</option>
            <option value="business">Geschäftskunden</option>
          </select>
          
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Status</option>
            <option value="active">Aktiv</option>
            <option value="inactive">Inaktiv</option>
            <option value="blocked">Gesperrt</option>
          </select>
          
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="name">Name</option>
            <option value="revenue">Umsatz</option>
            <option value="bookings">Buchungen</option>
            <option value="activity">Letzte Aktivität</option>
          </select>
          
          <button 
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn-sort"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
          
          <button onClick={fetchCustomers} className="btn-refresh">
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Customers Grid */}
      <div className="customers-grid">
        {sortedCustomers.length === 0 ? (
          <div className="no-data">
            <UserGroupIcon className="w-16 h-16 text-gray-400" />
            <p>Keine Kunden gefunden</p>
          </div>
        ) : (
          sortedCustomers.map(customer => (
            <div key={customer.id} className="customer-card">
              <div className="customer-card-header">
                <div className="customer-info">
                  <h3>
                    {customer.firstName} {customer.lastName}
                    {customer.vip && <span className="vip-badge">VIP</span>}
                  </h3>
                  <span className="customer-number">{customer.customerNumber}</span>
                </div>
                <div className="customer-badges">
                  {getCustomerTypeBadge(customer.customerType)}
                  {getStatusBadge(customer.status)}
                </div>
              </div>
              
              <div className="customer-card-body">
                <div className="contact-info">
                  <div className="contact-item">
                    <EnvelopeIcon className="w-4 h-4" />
                    <span>{customer.email}</span>
                  </div>
                  <div className="contact-item">
                    <PhoneIcon className="w-4 h-4" />
                    <span>{customer.phone}</span>
                  </div>
                  {customer.company && (
                    <div className="contact-item">
                      <BuildingOfficeIcon className="w-4 h-4" />
                      <span>{customer.company}</span>
                    </div>
                  )}
                  {customer.addresses[0] && (
                    <div className="contact-item">
                      <MapPinIcon className="w-4 h-4" />
                      <span>
                        {customer.addresses[0].postalCode} {customer.addresses[0].city}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="customer-stats">
                  <div className="stat-item">
                    <DocumentTextIcon className="w-4 h-4" />
                    <span>{customer.totalBookings} Buchungen</span>
                  </div>
                  <div className="stat-item">
                    <CreditCardIcon className="w-4 h-4" />
                    <span>{(customer.totalRevenue || 0).toFixed(2)} €</span>
                  </div>
                  <div className="stat-item">
                    <CalendarDaysIcon className="w-4 h-4" />
                    <span>Seit {new Date(customer.registeredAt).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
                
                {customer.tags.length > 0 && (
                  <div className="customer-tags">
                    {customer.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="customer-card-footer">
                <button 
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setShowDetails(true);
                  }}
                  className="btn-action btn-view"
                  title="Details anzeigen"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setFormData({
                      firstName: customer.firstName,
                      lastName: customer.lastName,
                      email: customer.email,
                      phone: customer.phone,
                      company: customer.company || '',
                      customerType: customer.customerType,
                      newsletter: customer.newsletter
                    });
                    // Initialize addresses for editing
                    setCustomerAddresses(customer.addresses.map((addr, index) => ({
                      ...addr,
                      id: addr.id || `existing-${index}`
                    })));
                    setShowEditModal(true);
                  }}
                  className="btn-action btn-edit"
                  title="Bearbeiten"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleSendEmail(customer)}
                  className="btn-action btn-email"
                  title="E-Mail senden"
                >
                  <EnvelopeIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleSendAccessLink(customer)}
                  className="btn-action btn-accesslink"
                  title="Zugangslink senden (Passwort festlegen, 7 Tage gültig)"
                  disabled={sendingAccessLinkId === customer.id}
                >
                  <KeyIcon className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleCall(customer)}
                  className="btn-action btn-phone"
                  title="Anrufen"
                >
                  <PhoneIcon className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDeleteCustomer(customer.id)}
                  className="btn-action btn-delete"
                  title="Löschen"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Customer Details Modal */}
      {showDetails && selectedCustomer && (
        <div className="modal-overlay" onClick={() => {
          setShowDetails(false);
          setSelectedAddressIndex(0);
        }}>
          <div className="modal-content customer-details" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                Kundendetails - {selectedCustomer.firstName} {selectedCustomer.lastName}
                {selectedCustomer.vip && <span className="vip-badge">VIP</span>}
              </h3>
              <button onClick={() => {
                setShowDetails(false);
                setSelectedAddressIndex(0);
              }} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-section">
                  <h4>Kontaktdaten</h4>
                  <p><strong>Kundennummer:</strong> <span>{selectedCustomer.customerNumber}</span></p>
                  <p><strong>Name:</strong> <span>{selectedCustomer.firstName} {selectedCustomer.lastName}</span></p>
                  <p><strong>E-Mail:</strong> <span>{selectedCustomer.email}</span></p>
                  <p><strong>Telefon:</strong> <span>{selectedCustomer.phone}</span></p>
                  {selectedCustomer.company && (
                    <p><strong>Firma:</strong> <span>{selectedCustomer.company}</span></p>
                  )}
                  <p><strong>Typ:</strong> <span>{selectedCustomer.customerType === 'business' ? 'Geschäftskunde' : 'Privatkunde'}</span></p>
                  <p><strong>Newsletter:</strong> <span>{selectedCustomer.newsletter ? 'Ja' : 'Nein'}</span></p>
                </div>
                
                <div className="detail-section">
                  <h4>Adressen</h4>
                  {selectedCustomer.addresses.length > 1 && (
                    <div style={{marginBottom: '10px'}}>
                      <select
                        value={selectedAddressIndex}
                        onChange={(e) => setSelectedAddressIndex(parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '5px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          marginBottom: '10px'
                        }}
                      >
                        {selectedCustomer.addresses.map((address, index) => (
                          <option key={index} value={index}>
                            {address.type === 'business' && 'Geschäftsadresse'}
                            {address.type === 'private' && 'Privatadresse'}
                            {address.type === 'billing' && 'Rechnungsadresse'}
                            {address.type === 'shipping' && 'Lieferadresse'}
                            {address.isDefault && ' (Standard)'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedCustomer.addresses[selectedAddressIndex] && (
                    <div className="address-item">
                      <p className="address-type">
                        {selectedCustomer.addresses[selectedAddressIndex].type === 'business' && 'Geschäftsadresse'}
                        {selectedCustomer.addresses[selectedAddressIndex].type === 'private' && 'Privatadresse'}
                        {selectedCustomer.addresses[selectedAddressIndex].type === 'billing' && 'Rechnungsadresse'}
                        {selectedCustomer.addresses[selectedAddressIndex].type === 'shipping' && 'Lieferadresse'}
                        {selectedCustomer.addresses[selectedAddressIndex].isDefault && ' (Standard)'}
                      </p>
                      <p><span>{selectedCustomer.addresses[selectedAddressIndex].street}</span></p>
                      <p><span>{selectedCustomer.addresses[selectedAddressIndex].postalCode} {selectedCustomer.addresses[selectedAddressIndex].city}</span></p>
                      <p><span>{selectedCustomer.addresses[selectedAddressIndex].country}</span></p>
                    </div>
                  )}
                  {selectedCustomer.addresses.length === 0 && (
                    <p><span style={{color: '#888'}}>Keine Adresse hinterlegt</span></p>
                  )}
                </div>
                
                <div className="detail-section">
                  <h4>Aktivität & Umsatz</h4>
                  <p><strong>Registriert:</strong> <span>{new Date(selectedCustomer.registeredAt).toLocaleDateString('de-DE')}</span></p>
                  <p><strong>Letzte Aktivität:</strong> <span>{new Date(selectedCustomer.lastActivity).toLocaleDateString('de-DE')}</span></p>
                  <p><strong>Anzahl Buchungen:</strong> <span>{selectedCustomer.totalBookings}</span></p>
                </div>

                <div className="detail-section full-width">
                  <h4>Gesamtumsatz</h4>
                  <ul className="notes-list">
                    <li>{selectedCustomer.totalRevenue.toFixed(2)} €</li>
                  </ul>
                </div>

                <div className="detail-section full-width">
                  <h4>Durchschnitt pro Buchung</h4>
                  <ul className="notes-list">
                    <li>{(selectedCustomer.totalRevenue / Math.max(selectedCustomer.totalBookings, 1)).toFixed(2)} €</li>
                  </ul>
                </div>
                
                {selectedCustomer.notes.length > 0 && (
                  <div className="detail-section full-width">
                    <h4>Notizen</h4>
                    <ul className="notes-list">
                      {selectedCustomer.notes.map((note, index) => (
                        <li key={index}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="modal-actions">
                <button 
                  onClick={() => {
                    setShowDetails(false);
                    setFormData({
                      firstName: selectedCustomer.firstName,
                      lastName: selectedCustomer.lastName,
                      email: selectedCustomer.email,
                      phone: selectedCustomer.phone,
                      company: selectedCustomer.company || '',
                      customerType: selectedCustomer.customerType,
                      newsletter: selectedCustomer.newsletter
                    });
                    // Initialize addresses for editing
                    setCustomerAddresses(selectedCustomer.addresses.map((addr, index) => ({
                      ...addr,
                      id: addr.id || `existing-${index}`
                    })));
                    setShowEditModal(true);
                  }}
                  className="btn-primary"
                >
                  Bearbeiten
                </button>
                <button onClick={() => handleSendEmail(selectedCustomer)} className="btn-secondary">
                  E-Mail senden
                </button>
                <button
                  onClick={() => handleSendAccessLink(selectedCustomer)}
                  className="btn-secondary"
                  disabled={sendingAccessLinkId === selectedCustomer.id}
                  title="Einladung mit Link zum Festlegen des Passworts (7 Tage gültig)"
                >
                  {sendingAccessLinkId === selectedCustomer.id ? 'Wird gesendet...' : 'Zugangslink senden'}
                </button>
                <button className="btn-secondary">
                  Buchungshistorie anzeigen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit/New Customer Modal */}
      {(showEditModal || showNewCustomerModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowEditModal(false);
          setShowNewCustomerModal(false);
        }}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
              <button onClick={() => {
                setShowEditModal(false);
                setShowNewCustomerModal(false);
                setCustomerAddresses([]);
                setEditingAddress(null);
                setShowAddressForm(false);
              }} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleSaveCustomer();
              }}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Vorname *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Nachname *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>E-Mail *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Telefon *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Firma</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Kundentyp</label>
                    <select
                      value={formData.customerType}
                      onChange={(e) => setFormData({...formData, customerType: e.target.value as 'private' | 'business'})}
                    >
                      <option value="private">Privatkunde</option>
                      <option value="business">Geschäftskunde</option>
                    </select>
                  </div>

                  {/* Address Management */}
                  <div className="form-group full-width">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                      <h4 style={{fontSize: '16px', color: '#1a2332', margin: 0}}>Adressen</h4>
                      <button
                        type="button"
                        onClick={addNewAddress}
                        className="btn-primary"
                        style={{padding: '5px 10px', fontSize: '12px'}}
                      >
                        + Neue Adresse
                      </button>
                    </div>
                  </div>

                  {/* List existing addresses */}
                  {customerAddresses.length > 0 && (
                    <div className="form-group full-width" style={{marginBottom: '20px'}}>
                      {customerAddresses.map((addr, index) => (
                        <div key={addr.id} style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '10px',
                          marginBottom: '10px',
                          background: addr.isDefault ? '#f0f8ff' : '#fff'
                        }}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                            <div style={{flex: 1}}>
                              <strong style={{color: '#1a2332'}}>
                                {addr.type === 'private' && 'Privatadresse'}
                                {addr.type === 'business' && 'Geschäftsadresse'}
                                {addr.type === 'billing' && 'Rechnungsadresse'}
                                {addr.type === 'shipping' && 'Lieferadresse'}
                                {addr.isDefault && ' (Standard)'}
                              </strong>
                              <p style={{margin: '5px 0'}}>{addr.street}</p>
                              <p style={{margin: '5px 0'}}>{addr.postalCode} {addr.city}</p>
                              <p style={{margin: '5px 0'}}>{addr.country}</p>
                            </div>
                            <div style={{display: 'flex', gap: '5px'}}>
                              {!addr.isDefault && (
                                <button
                                  type="button"
                                  onClick={() => setAsDefault(addr.id)}
                                  style={{
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Als Standard
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingAddress(addr);
                                  setShowAddressForm(true);
                                }}
                                style={{
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer'
                                }}
                              >
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteAddress(addr.id)}
                                style={{
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer'
                                }}
                              >
                                Löschen
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Address Form (shown when adding/editing) */}
                  {showAddressForm && editingAddress && (
                    <div className="form-group full-width" style={{
                      border: '2px solid #007bff',
                      borderRadius: '4px',
                      padding: '15px',
                      marginBottom: '20px',
                      background: '#f8f9fa'
                    }}>
                      <h5 style={{marginBottom: '10px'}}>
                        {editingAddress.id.startsWith('new') ? 'Neue Adresse hinzufügen' : 'Adresse bearbeiten'}
                      </h5>

                      <div className="form-group">
                        <label>Adresstyp</label>
                        <select
                          value={editingAddress.type}
                          onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            type: e.target.value as 'private' | 'business' | 'billing' | 'shipping'
                          })}
                        >
                          <option value="private">Privatadresse</option>
                          <option value="business">Geschäftsadresse</option>
                          <option value="billing">Rechnungsadresse</option>
                          <option value="shipping">Lieferadresse</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Straße & Hausnummer</label>
                        <input
                          type="text"
                          value={editingAddress.street}
                          onChange={(e) => setEditingAddress({...editingAddress, street: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>PLZ</label>
                        <input
                          type="text"
                          value={editingAddress.postalCode}
                          onChange={(e) => setEditingAddress({...editingAddress, postalCode: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Stadt</label>
                        <input
                          type="text"
                          value={editingAddress.city}
                          onChange={(e) => setEditingAddress({...editingAddress, city: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Land</label>
                        <input
                          type="text"
                          value={editingAddress.country}
                          onChange={(e) => setEditingAddress({...editingAddress, country: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={editingAddress.isDefault}
                            onChange={(e) => setEditingAddress({...editingAddress, isDefault: e.target.checked})}
                          />
                          Als Standardadresse setzen
                        </label>
                      </div>

                      <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
                        <button
                          type="button"
                          onClick={saveAddress}
                          className="btn-primary"
                          style={{padding: '8px 15px'}}
                        >
                          Speichern
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingAddress(null);
                            setShowAddressForm(false);
                          }}
                          className="btn-secondary"
                          style={{padding: '8px 15px'}}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.newsletter}
                        onChange={(e) => setFormData({...formData, newsletter: e.target.checked})}
                      />
                      Newsletter abonnieren
                    </label>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {selectedCustomer ? 'Speichern' : 'Kunde anlegen'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowEditModal(false);
                      setShowNewCustomerModal(false);
                      setCustomerAddresses([]);
                      setEditingAddress(null);
                      setShowAddressForm(false);
                    }}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;